import { ADMIN_API_PREFIX, route } from "./routes.js";
import { z } from "zod";
import { AUDIT_ACTOR_FILTERS, AUDIT_ACTOR_TYPES, AUDIT_LEVELS, AUDIT_LOG_SOURCES, AUDIT_READER_CURSOR_RE, AUDIT_READER_MAX_CURSOR_CHARS, AUDIT_READER_MAX_EVENT_TYPES, AUDIT_READER_MAX_EVENT_TYPE_CHARS, auditCanonicalIdSchema } from "@zion/sdk-contracts";
//#region src/audit-log.ts
/**
* `GET /v1/admin/audit-log` — one page of the org's administrative history, plus the view the
* server resolved.
*
* Producer `AuditController` in core-api, under `src/audit/`. Permission `audit:read` on the
* class, and the org comes from the verified JWT, never from a query param.
*
* The row type is `AdminAuditEvent`, prefixed because `@zion/sdk-contracts` exports an unrelated
* `AuditEvent`: that one is the CP-only `cp-auth-api` `/internal/audit` shape and shares no member
* with this one. The audit vocabulary itself — levels, actor filters, the actor-type and source
* unions — is imported from `@zion/sdk-contracts`, which is its one home.
*
* Two facts the shell's `api/client.ts` does not carry, and the controller is the fact. The
* response row has an `actorType` member the shell's `AuditEvent` omits, so the shell drops what
* the writer asserted about the principal that acted. And the query accepts a ninth key,
* `includeSystem`, which is deprecated for external callers and which the shell no longer sends.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client.
* No shape in this file travels in both directions.
*/
/** The only path in this family. No parameter. */
const ADMIN_AUDIT_LOG_PATH = `${ADMIN_API_PREFIX}/audit-log`;
/**
* `GET /v1/admin/audit-log`. One page of merged audit rows for the caller's org, newest first.
* Permission `audit:read`.
*/
const LIST_AUDIT_LOG_ROUTE = route("GET", ADMIN_AUDIT_LOG_PATH);
/**
* Server paging bound: the one declaration. core-api's `audit-log-query.schema.ts` imports and
* re-exports it, so the two sides cannot disagree (A-arch-P-2, R-59).
*/
const AUDIT_LOG_MAX_PAGE_SIZE = 200;
/** The largest page number the door admits, so the computed offset stays a safe integer. */
const AUDIT_LOG_MAX_PAGE = 1e5;
/** `'people' | 'system'` — the row's derived actor class. */
const adminAuditRowActorSchema = z.enum(["people", "system"]);
/** `'human' | 'agent' | 'worker' | 'service' | 'unknown'` — the stored actor classification. */
const adminAuditActorTypeSchema = z.enum(AUDIT_ACTOR_TYPES);
/** `'platform' | 'app'` — which system authored the row. */
const adminAuditLogSourceSchema = z.enum(AUDIT_LOG_SOURCES);
/**
* One DP admin audit row, as the controller projects it field by field.
*
* The controller maps members explicitly, so anything absent from its map is dropped before the
* wire. Every member below is in that map.
*/
const adminAuditEventSchema = z.object({
	id: z.string(),
	/** The event type, `auth.audit_log.action`. */
	type: z.string(),
	/** Who acted. Null on a row whose emit site threaded no operator through. */
	userId: z.string().nullable(),
	/** The actor resolved to an address, read-time from `auth.users`. Null for a machine actor. */
	userEmail: z.string().nullable(),
	ipAddress: z.string().nullable(),
	/** The row's `details` bag, sanitised server-side. Null when the emit site wrote none. */
	metadata: z.record(z.string(), z.unknown()).nullable(),
	/** ISO-8601. The controller holds a `Date` and serialises it. */
	createdAt: z.string(),
	/**
	* Whether a person or the platform performed this event, classified from the event type. Not
	* the same question as "is `userId` set": a human administrative action can carry no actor id,
	* so deriving this from a null `userId` prints "System" over real admin activity.
	*/
	actor: adminAuditRowActorSchema,
	/**
	* What the emit site asserted about the principal that acted, or null when unclassified.
	*
	* Not a duplicate of `actor`: `actor` is derived from the event type and is identical for every
	* row of that type. This is what lets a reader render `agent · a1b2c3` where `userEmail` is
	* null, which is the majority of production volume.
	*/
	actorType: adminAuditActorTypeSchema.nullable(),
	/** The subject — the thing the action was done to. Null when the row records none. */
	resourceId: z.string().nullable(),
	resourceType: z.string().nullable(),
	/**
	* The subject resolved to a human address. Null is normal: the server resolves human subjects
	* only, so every other subject kind and every deleted account reads null. Render
	* `resourceEmail ?? resourceId`, never this member alone.
	*/
	resourceEmail: z.string().nullable(),
	/**
	* Which code path wrote the row. `'internal-audit-ingest'` or
	* `` `internal-audit-ingest:${caller}` `` means the row arrived through `POST /internal/audit`.
	* Match it as a prefix, never with `===`: the producer stamps the qualified form and the bare
	* form stays in the trail forever, so an equality test recognises historic rows and misses
	* every new one.
	*/
	emitter: z.string().nullable(),
	/**
	* Which system authored the row. Not `emitter`, which names the code path and is free-form.
	* Not nullable: the column is `NOT NULL DEFAULT 'platform'`.
	*/
	source: adminAuditLogSourceSchema,
	/**
	* The verb's declared label, resolved server-side from the app's manifest declaration, or null
	* when the verb has no declaration. Null for every platform verb and for an undeclared app
	* verb, so a renderer needs its own fallback ladder.
	*/
	label: z.string().nullable(),
	/**
	* The second identity the row names, beside its actor and its subject: for example the member
	* of a `group_member_added` row, the role definition of a `user_role_granted` row, or the child
	* group of a `group_child_added` row.
	*
	* core-api reads it from the archive column `related_object_id`. The envelope builder fills
	* that column from the `details` key that the audit vocabulary declares as `related` for the
	* event type (`AUDIT_EVENT_CLASSIFICATION` in `@zion/sdk-contracts`). A `userId` query matches
	* this member as its third column.
	*
	* The value grammar is `auditCanonicalIdSchema` from `@zion/sdk-contracts`: lower-case
	* 8-4-4-4-12 hex, with no check of the RFC 4122 version or variant nibble. The envelope builder
	* fills the column through `isAuditCanonicalId`, and the envelope schema bounds
	* `relatedObjectId` with the same schema. So every id the producer can write parses here, for
	* example the seed id `00000000-0000-0000-0000-000000000001`. A value outside that grammar
	* (upper case, no hyphens, a slug, an address) is refused, because no producer writes one.
	*
	* A canonical id, or null. Null is normal in three cases, and the reader cannot tell them apart:
	* 1. The event type declares `related: null` or no related key.
	* 2. The declared key held no value, or a value outside the lower-case uuid grammar. The
	*    builder records no related id then, and never refuses the event.
	* 3. core-api wrote the row before it filled the column. `relatedSince` on the response
	*    bounds that case.
	*
	* The member is absent (not null) on a row from a core-api that predates this member. The
	* schema accepts that row and keeps the member absent, so one admin app parses the pages of
	* data planes on different core-api versions (defaults row 9 of the plan
	* 2026-10-06-audit-second-id-filter). A consumer must treat an absent member as unknown.
	*
	* The member stays on the row after an erasure of the row's subject key, because the column is
	* clear text and not one of the sealed columns.
	*/
	relatedObjectId: auditCanonicalIdSchema.nullable().optional()
});
/**
* A cursor at this door: 1 to `AUDIT_READER_MAX_CURSOR_CHARS` (512) characters of base64url with no
* padding, the reader wire's own bounds. core-api passes it to the reader unread; this door
* refuses a cursor the reader would refuse for its shape (activity-paging decision 5).
*/
const auditLogCursorSchema = z.string().min(1).max(AUDIT_READER_MAX_CURSOR_CHARS).regex(AUDIT_READER_CURSOR_RE, "cursor: base64url with no padding");
/**
* Reads the `eventTypes` query value as a list. A query string carries it as one comma-joined
* string (`eventTypes=a,b,c`), which this splits on `,`. A repeated key arrives as an array from
* the query parser, and each string entry splits on `,` too, so `eventTypes=a,b&eventTypes=c`
* reads as three types and no entry can hold a comma. Any other value passes unchanged, so the
* array schema refuses it. It is a `z.preprocess` and not a `.transform()`: zod 4.4.3 emits a
* JSON Schema for a preprocess (the output side, an array), and throws on a transform, which
* core-api runs at boot (`createOpenApiDocument`).
*/
function readEventTypeList(value) {
	if (typeof value === "string") return value.split(",");
	if (Array.isArray(value)) return value.flatMap((entry) => typeof entry === "string" ? entry.split(",") : [entry]);
	return value;
}
/**
* Reads the `includeTotal` query value as a boolean: exactly `'true'` is `true` and exactly
* `'false'` is `false`. A boolean passes as it is. Any other value passes unchanged, so the
* boolean schema refuses it (`'1'`, `'yes'`, `'TRUE'`, `''`). `z.coerce.boolean()` is wrong here:
* it reads the non-empty string `'false'` as `true`, which turns the keyset opt-out into an exact
* total (measured on zod 4.4.3, the DEC-13 inversion that `includeSystem` also avoids).
*/
function readQueryBoolean(value) {
	if (value === "true") return true;
	if (value === "false") return false;
	return value;
}
/**
* The grammar of the `userId` filter (R-031 and R-033 of the plan 2026-10-06-audit-second-id-filter):
* 8-4-4-4-12 hex digits in either case, with no check of the RFC 4122 version or variant
* nibble.
*
* It is `auditCanonicalIdSchema` (`AUDIT_CANONICAL_ID_PATTERN` in `@zion/sdk-contracts`) with
* upper case added. So the filter accepts every `relatedObjectId` that a page returns, for
* example the seed id `00000000-0000-0000-0000-000000000001`, and every id that `z.uuid()`
* accepted before. It admits upper case because `object_id` is free text that keeps its
* writer's case, and core-api sends the value unchanged (R-033). The class lists both cases, and
* the pattern carries no `i` flag, because the JSON Schema `pattern` that core-api emits holds
* the source text alone and drops the flags.
*/
const AUDIT_LOG_ID_FILTER_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
/**
* `GET /v1/admin/audit-log` query, mirroring core-api's `auditLogQuerySchema`.
*
* `startDate` and `endDate` are wire strings, deliberately not `z.coerce.date()` and not
* `z.iso.datetime()`: a coerced date is not JSON-Schema-representable and breaks OpenAPI
* emission, and the shell sends `YYYY-MM-DD` from an `<input type="date">`. The controller
* reads the string the caller sent and accepts an ISO-8601 instant in the
* `z.iso.datetime({ offset: true })` spelling (`Z` or a `±HH:MM` offset through ±23:59) or a
* `YYYY-MM-DD` day read as UTC midnight, with a four-digit year of 0001 or later once rendered in
* UTC (`parseWireQueryBound`). It answers 400 for anything else, `September 1, 2026` included. The
* 400 body is `{ error, code, value }`, where `error` and `code` are both `invalid_start_date` or both
* `invalid_end_date`. The global exception filter adds `statusCode`, `message`, `timestamp` and `path`.
*
* `page` carries an explicit `.max(100000)` so the computed offset cannot reach Postgres as an
* unsafe integer. `includeSystem` is a literal union and not a boolean coercion: `'false'` is a
* non-empty string and would coerce to `true`, inverting the request. It is also not a
* `.transform()`, because `z.toJSONSchema` throws on any transform and core-api emits its OpenAPI
* document at boot, so a transform here crash-loops every pod.
*
* The keyset mode (2026-10-06-activity-paging decisions 1 to 6) adds three keys, all optional:
* `eventTypes`, `cursor` and `includeTotal`. A request with a `cursor` or `includeTotal: false`
* is the keyset mode; every other request is the offset mode it was, with exact counts. The two
* value conversions (`eventTypes` to a list, `includeTotal` to a boolean) are `z.preprocess`
* steps, which emit a JSON Schema, and never `.transform()`. Three requests are refused with 400,
* each on the named path:
* 1. `eventType` with `eventTypes` (path `eventTypes`).
* 2. The keyset mode with a `page` other than 1 (path `page`).
* 3. A `cursor` with `includeTotal: true` (path `includeTotal`).
* The reader wire, `auditReaderQueryRequestV1Schema`, refuses the same three.
*/
const listAuditLogRequestSchema = z.strictObject({
	/**
	* Filter to one id: a person, a machine principal, a group or a role definition. A row
	* matches when the id is in one of three archive columns: `actor_id` (the row's `userId`),
	* `object_id` (`resourceId`) or `related_object_id` (`relatedObjectId`).
	*
	* The grammar is {@link AUDIT_LOG_ID_FILTER_PATTERN}: 8-4-4-4-12 hex in either case. core-api
	* sends the value to the reader as the caller sent it, and the reader compares each column byte
	* for byte (R-031 as R-033 amends it). `actor_id` and `related_object_id` hold the lower-case
	* grammar. `object_id` is free text that keeps the case its writer gave it. So an upper-case id
	* finds a row whose `object_id` holds that exact text, and it matches no actor and no related
	* id.
	*
	* `relatedSince` on the response is null when no segment that the read scanned holds a related
	* id. Otherwise it is the earliest such segment start plus 24 hours, with two exceptions that
	* give null. First, the reader predates the related column and reports no related segment.
	* Second, the sum passes `9999-12-31T23:59:59.999Z`, the last instant with a four-digit year.
	* Null then claims no coverage. An instant can come back when no row matches this id.
	*
	* core-api writes the same text into `cloud/dp/core-api/openapi.json` twice, and core-api's
	* `contract-snapshot.spec.ts` compares that file with the document it emits from this schema.
	* A change to the text here changes that file in the same change.
	*/
	userId: z.string().regex(AUDIT_LOG_ID_FILTER_PATTERN, "userId: 8-4-4-4-12 hex").optional().describe("Filter to one id: a person, a machine principal, a group or a role definition. An 8-4-4-4-12 hex id in either case, matched as sent, byte for byte: actor_id and related_object_id hold lower case, and object_id keeps the case its writer gave it. A row matches when the id is its actor, its subject or its related id: the archive columns actor_id, object_id and related_object_id, returned as userId, resourceId and relatedObjectId. Answers \"what did it do\", \"what was done to it\" and \"which rows name it as a second party\", for example the member of a group_member_added row. Older rows hold no related id and match on actor_id and object_id only. relatedSince on the response is the instant from which the related match is complete. It is null when no archive segment that the read scanned holds a related id. Otherwise it is the earliest such segment start plus 24 hours, with two exceptions that give null. First, the reader predates the related column and reports no related segment. Second, the sum passes 9999-12-31T23:59:59.999Z, the last instant with a four-digit year. Null then claims no coverage. It can be an instant when no row matches the id."),
	/** Exact event type, `auth.audit_log.action`. */
	eventType: z.string().min(1).max(256).optional().describe("Exact event type — audit_log.action."),
	/** Inclusive lower bound on `created_at`: an ISO-8601 instant (`Z` or a `±HH:MM` offset through ±23:59) or a `YYYY-MM-DD` day, with a four-digit year of 0001 or later, else 400 `invalid_start_date`. */
	startDate: z.string().optional().describe("Inclusive lower bound on created_at: an ISO-8601 instant (Z or a +-HH:MM offset through +-23:59) or a YYYY-MM-DD day, with a four-digit year of 0001 or later, else 400 invalid_start_date."),
	/** Inclusive upper bound on `created_at`: an ISO-8601 instant (`Z` or a `±HH:MM` offset through ±23:59) or a `YYYY-MM-DD` day, with a four-digit year of 0001 or later, else 400 `invalid_end_date`. */
	endDate: z.string().optional().describe("Inclusive upper bound on created_at: an ISO-8601 instant (Z or a +-HH:MM offset through +-23:59) or a YYYY-MM-DD day, with a four-digit year of 0001 or later, else 400 invalid_end_date."),
	/** 1-based page number. Default 1 at the service. */
	page: z.coerce.number().int().min(1).max(AUDIT_LOG_MAX_PAGE).optional().describe("1-based page number (1-100000, default 1)."),
	/** Rows per page. Default 20 at the service. */
	pageSize: z.coerce.number().int().min(1).max(200).optional().describe("Rows per page (1-200, default 20)."),
	/**
	* Minimum detail level as an ordered threshold: security > admin > activity > system. Returns
	* this level and above. Default `'activity'`, which excludes the two system-level types that
	* dominate the table by volume.
	*/
	level: z.enum(AUDIT_LEVELS).optional().describe("Minimum detail level, as an ordered threshold: security > admin > activity > system. Returns this level AND ABOVE. Defaults to 'activity', which excludes the two system-level types that dominate the table by volume."),
	/**
	* Who acted: `'people'`, `'system'` or `'all'`. Default `'all'`. Filtering to `'people'` by
	* default hid every machine-actor security row, so it is opt-in.
	*/
	actor: z.enum(AUDIT_ACTOR_FILTERS).optional().describe("Who acted: 'people', 'system', or 'all'. Defaults to 'all'. Pairs with level — the highest-volume event types are also the most security-relevant, so a level threshold alone does not separate human activity from machine activity. Filtering to 'people' by default hid every machine-actor security row, so it is opt-in."),
	/**
	* Deprecated, superseded by `level` and `actor`. `'true'` is equivalent to
	* `level=system&actor=all`. An explicit `level` or `actor` wins over it. The shell no longer
	* sends it; the door keeps it for external callers.
	*/
	includeSystem: z.enum(["true", "false"]).optional().describe("DEPRECATED — superseded by 'level' and 'actor'. 'true' is equivalent to level=system&actor=all, i.e. show everything. An explicit 'level' or 'actor' wins over it."),
	/**
	* A list of exact event types (activity-paging decision 6): a row passes when its type is one
	* of them. 1 to 64 entries of 1 to 256 characters. On the wire it is one comma-joined value,
	* `eventTypes=a,b,c`; the schema reads it to a `string[]`. Exclusive with `eventType`.
	*/
	eventTypes: z.preprocess(readEventTypeList, z.array(z.string().min(1).max(AUDIT_READER_MAX_EVENT_TYPE_CHARS).regex(/^\S+$/, "eventTypes: an event type holds no whitespace")).min(1).max(AUDIT_READER_MAX_EVENT_TYPES)).optional().describe("Exact event types, comma-joined (1-64 entries of 1-256 characters, no whitespace). A row passes when audit_log.action is one of them. Exclusive with eventType."),
	/**
	* The next page's position, copied from the previous answer's `nextCursor` (decision 5).
	* Opaque: the reader mints it and binds it to the org, the window and the filters, so a cursor
	* sent with a changed filter is a 400. Selects the keyset mode; `page` must then be 1 or absent.
	*
	* A cursor this door admits and the reader refuses (bytes that are not a cursor, or a cursor
	* minted for another org, window or filter set) reaches the caller as a 400 with the code
	* `audit_cursor_refused` and a fixed message that quotes no cursor byte (activity-paging
	* R-003). The caller drops the cursor and reads the first page again with its filters.
	*/
	cursor: auditLogCursorSchema.optional().describe("The previous answer's nextCursor, unchanged, to read the next page with the same filters. Opaque, 1-512 base64url characters. Selects the keyset mode: page must be 1 or absent, and includeTotal must not be 'true'."),
	/**
	* Whether the answer owes an exact `total` and `filteredOut` (decision 3). `'false'` selects the
	* keyset mode, where they are floors and `countsExact` is `false`. `'true'` or absent keeps the
	* offset mode with exact counts. The wire spells `'true'` or `'false'`; the schema reads a
	* boolean.
	*/
	includeTotal: z.preprocess(readQueryBoolean, z.boolean()).optional().describe("'false' selects the keyset mode: newest first, nextCursor for the next page, total and filteredOut as floors (countsExact false). 'true' or absent: offset paging with exact counts."),
	/**
	* Filter to one app by its catalog id, the `release.apps_catalog` UUID (2026-10-21-app-open-audit,
	* D12). A row matches when its `resourceId` (the archive column `object_id`) is that id. It ANDs
	* with every other filter, so `eventType=app_opened` with `userId` and `appId` gives one person's
	* opens of one app. It also returns the other rows whose subject is that app, for example
	* `app_token_minted` and `app_adoption.*`.
	*
	* It also returns the `resource_grant.granted` and `resource_grant.revoked` rows of the app's
	* permission grants, revoked grants included (2026-10-31-activity-app-filter). A grant row's
	* `resourceId` is the grant id. So core-api reads the ids of the app's grant rows and sends them
	* to the reader as `objectIdAliases`. The filter does not see any other row that names its app
	* only in sealed details or metadata. Past 1024 grant rows, core-api answers 422
	* `audit_filter_overflow`.
	*
	* The grammar is {@link AUDIT_LOG_ID_FILTER_PATTERN}, the grammar of `userId`. core-api sends the
	* value to the reader unchanged, as the `objectId` term, and the reader compares it byte for
	* byte. The `app_opened` row writes the catalog id in lower case, so an upper-case id matches no
	* such row. A cursor minted under one `appId` is refused under another.
	*
	* No `.transform()`: core-api emits its OpenAPI document from this schema at boot, and
	* `cloud/dp/core-api/openapi.json` carries this text.
	*/
	appId: z.string().regex(AUDIT_LOG_ID_FILTER_PATTERN, "appId: 8-4-4-4-12 hex").optional().describe("Filter to one app by its catalog id (release.apps_catalog). An 8-4-4-4-12 hex id, matched as sent, byte for byte, against resourceId (the archive column object_id). ANDs with every other filter: eventType=app_opened with userId and appId gives one person's opens of one app. It also returns the other rows whose subject is that app. It also returns the resource_grant.granted and resource_grant.revoked rows of the app's permission grants. These rows include the rows of revoked grants. It does not match any other row that names its app only in sealed details or metadata.")
}).refine((q) => q.eventType === void 0 || q.eventTypes === void 0, {
	message: "eventType and eventTypes are exclusive",
	path: ["eventTypes"]
}).refine((q) => q.cursor === void 0 && q.includeTotal !== false || q.page === void 0 || q.page === 1, {
	message: "the keyset mode (a cursor, or includeTotal 'false') reads page 1 only",
	path: ["page"]
}).refine((q) => q.cursor === void 0 || q.includeTotal !== true, {
	message: "a cursor page owes no exact total, so includeTotal must not be 'true'",
	path: ["includeTotal"]
});
/**
* `GET /v1/admin/audit-log` response: the page, plus the effective view.
*
* `page`, `pageSize`, `level` and `actor` are the values the query actually ran with, so a client
* that sent nothing learns which defaults it got instead of hard-coding them. `filteredOut` is
* how many rows the level and actor controls hid over the caller's own other filters: a narrowed
* audit view that cannot say what it is hiding is indistinguishable from a complete one.
*
* The keyset mode adds three optional members (activity-paging decisions 3 to 5). `total` stays
* required in both modes, so the paged envelope pin below holds. Which members an answer carries:
* 1. Offset mode, no `includeTotal`: none of the three; the answer is today's.
* 2. Offset mode, `includeTotal: true`: `countsExact: true`.
* 3. Keyset mode: `nextCursor`, `searchedBackTo` and `countsExact: false`; `total` and
*    `filteredOut` are then floors over the span searched, never the window's exact counts.
* The schema leaves the combination unchecked (R-28): a client reads `countsExact !== false` as
* exact counts and `nextCursor !== null` as "more rows may remain".
*/
const listAuditLogResponseSchema = z.object({
	data: z.array(adminAuditEventSchema),
	total: z.number().int().nonnegative(),
	page: z.number().int().min(1),
	pageSize: z.number().int().min(1),
	level: z.enum(AUDIT_LEVELS),
	actor: z.enum(AUDIT_ACTOR_FILTERS),
	filteredOut: z.number().int().nonnegative(),
	/**
	* The instant from which a `userId` read matches the related column completely, as an ISO-8601
	* instant in the `Z` form (`z.iso.datetime()`), or null.
	*
	* core-api sets it to the reader's `relatedFilledFrom` plus 24 hours. It sets null in two
	* cases:
	* 1. The reader answers no `relatedFilledFrom`.
	* 2. The sum passes `9999-12-31T23:59:59.999Z`, the last instant with a four-digit year. A
	*    capped earlier instant would cut the 24-hour margin, and null claims no coverage.
	* The reader sets `relatedFilledFrom` on a `userId` read only. It is the earliest
	* `min_recorded_at` of a scanned segment whose `related_object_id` holds a value
	* (`auditReaderQueryResponseV1Schema` in `@zion/sdk-contracts`). So a read with no `userId`
	* always answers null.
	*
	* What a consumer may rely on:
	* 1. A row at or after this instant carries `relatedObjectId` when its event type declares a
	*    related key and the key held a valid id. The 24 hours cover a core-api rollout that
	*    settles within 24 hours of its first filled row.
	* 2. A row before this instant can lack `relatedObjectId` that a newer core-api would fill. A
	*    consumer that needs those rows must read them by another filter.
	* 3. The value is a lower bound of the reader's own first segment, unclamped, so it can precede
	*    the request's `startDate` (R-004 of the plan 2026-10-06-audit-second-id-filter).
	* 4. An `eventType` or `eventTypes` filter can make it later, or null, for the same window. It
	*    is never earlier than the unfiltered value, so it never claims coverage that the archive
	*    lacks.
	* 5. Both modes carry it. In the keyset mode the reader computes `relatedFilledFrom` over the
	*    segments of that one page (2026-10-06-activity-paging), so a keyset page can answer a
	*    later instant than the offset mode for the same window, and never an earlier one.
	*
	* The member is absent (not null) on a response from a core-api that predates this member. The
	* schema accepts that response and keeps the member absent, so one admin app parses the pages
	* of data planes on different core-api versions (defaults row 9 of the plan
	* 2026-10-06-audit-second-id-filter). An absent member says nothing about related coverage.
	*/
	relatedSince: z.iso.datetime().nullable().optional(),
	/**
	* Keyset mode only. The `cursor` for the next page with the same filters, or `null` at the end
	* of the window. A string can come with fewer than `pageSize` rows: the search hit its work
	* bound or the per-query budget, and the caller may continue with it. When the first day a
	* keyset page covers passes the per-query budget alone, the route answers the 422
	* `window_too_wide` of the offset mode in place of a page (activity-paging R-022).
	*/
	nextCursor: auditLogCursorSchema.nullable().optional(),
	/**
	* Keyset mode only. The oldest instant this page's search covered, an ISO instant; `null` when
	* it covered the window back to its start. A client shows "Searched back to <date>" while it
	* continues.
	*/
	searchedBackTo: z.string().datetime().nullable().optional(),
	/** Whether `total` and `filteredOut` are exact over the window; absent means exact. */
	countsExact: z.boolean().optional()
});
/** Every route this file declares, for the package's route census. */
const auditLogRoutes = Object.freeze([LIST_AUDIT_LOG_ROUTE]);
//#endregion
export { ADMIN_AUDIT_LOG_PATH, AUDIT_LOG_MAX_PAGE, AUDIT_LOG_MAX_PAGE_SIZE, LIST_AUDIT_LOG_ROUTE, adminAuditActorTypeSchema, adminAuditEventSchema, adminAuditLogSourceSchema, adminAuditRowActorSchema, auditLogRoutes, listAuditLogRequestSchema, listAuditLogResponseSchema };

//# sourceMappingURL=audit-log.js.map